import { Button, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useIsFetching, useQueryClient } from 'react-query';

import Layout from '../../../components/Layout/Layout';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import PageSubtitle from '../../../components/PageSubtitle/PageSubtitle';

import useLocale from '../../../hooks/useLocale';
import { INTEGRATION_MONITORING_QUERY } from '../../../constants/reactQuery';
import { ROUTES } from '../../../constants/routes';
import CommandsTab from './CommandsTab';
import EventsTab from './EventsTab';
import SummaryPanel from './SummaryPanel';
import { COMMANDS_TAB_KEY, EVENTS_TAB_KEY } from './constants';
import { useIntegrationSummary } from './useIntegrationMonitoring';

import './IntegrationMonitoring.scss';

function IntegrationMonitoring(): JSX.Element {
  const { t } = useLocale();
  const queryClient = useQueryClient();
  const summaryQuery = useIntegrationSummary();
  const isRefreshing = useIsFetching([INTEGRATION_MONITORING_QUERY]) > 0;

  const tabs: TabsProps['items'] = [
    { key: COMMANDS_TAB_KEY, label: t.INTEGRATION_TAB_COMMANDS, children: <CommandsTab /> },
    {
      key: EVENTS_TAB_KEY,
      label: t.INTEGRATION_TAB_EVENTS,
      children: <EventsTab isIntegrationEnabled={summaryQuery.data?.insolvIntegrationEnabled} />,
    },
  ];

  return (
    <Layout>
      <Breadcrumb
        data={[
          { title: t.INTEGRATION_BREADCRUMB_HOME, link: ROUTES.DASHBOARD },
          { title: t.ADMINISTRATION },
          { title: t.INTEGRATION_MONITORING },
        ]}
      />
      <PageSubtitle
        title={t.INTEGRATION_MONITORING}
        leftSideComponent={
          <Button
            icon={<ReloadOutlined />}
            loading={isRefreshing}
            onClick={() => queryClient.invalidateQueries([INTEGRATION_MONITORING_QUERY])}
          >
            {t.INTEGRATION_REFRESH}
          </Button>
        }
      />

      <div className="integration-monitoring">
        <SummaryPanel
          summary={summaryQuery.data}
          isLoading={summaryQuery.isLoading}
          isError={summaryQuery.isError}
          error={summaryQuery.error}
          onRetry={() => summaryQuery.refetch()}
        />
        <Tabs items={tabs} />
      </div>
    </Layout>
  );
}

export default IntegrationMonitoring;
